import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus, Pencil, Trash2, Search } from "lucide-react";
import { SchemaField } from "./SchemaField";
import { emptyFor } from "./schemas";

const daysTo = (iso) => (iso && iso.length >= 10 ? Math.round((new Date(iso.slice(0, 10)) - new Date(new Date().toDateString())) / 86400000) : null);

export function ScadCell({ value, fmt }) {
  const d = daysTo(value);
  if (d === null) return <span className="text-slate-300">—</span>;
  const cls = d < 0 ? "bg-red-100 text-red-700" : d <= 7 ? "bg-orange-100 text-orange-700" : d <= 30 ? "bg-amber-100 text-amber-700" : "text-slate-700";
  return <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-semibold ${cls}`}>{fmt(value)}</span>;
}

export function CellValue({ f, item }) {
  const v = item[f.k];
  if (f.t === "quals") return <span className="font-mono text-xs">{(v || []).join("/") || <span className="text-slate-300">—</span>}</span>;
  if (f.t === "list") return <span className="font-mono text-xs">{(v || []).join(", ")}</span>;
  if (f.scad) return <ScadCell value={v} fmt={f.fmt} />;
  if (f.t === "select") return f.options.find((o) => o.v === v)?.l || v || "";
  if (f.fmt) return f.fmt(v);
  return <span className={f.mono ? "font-mono text-xs uppercase" : ""}>{v ?? ""}</span>;
}

// Iter 105: tabella + form generici guidati dallo schema (CRUD /api/risorse/{kind})
export default function ResourceTable({ schema, isAdmin, sedi, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const cols = schema.fields.filter((f) => f.col);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/risorse/${schema.kind}`, { params: { include_inattivi: true } });
      setItems(data || []);
    } catch { toast.error("Errore nel caricamento"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [schema.kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((it) => schema.searchKeys.some((k) => String(Array.isArray(it[k]) ? it[k].join(" ") : it[k] || "").toLowerCase().includes(s)));
  }, [items, q, schema]);

  const save = async () => {
    const req = schema.fields.find((f) => f.req && !String(editing[f.k] || "").trim());
    if (req) return toast.error(`Compila il campo "${req.l}"`);
    setSaving(true);
    try {
      if (editing.id) await api.put(`/risorse/${schema.kind}/${editing.id}`, editing);
      else await api.post(`/risorse/${schema.kind}`, editing);
      toast.success("Salvato");
      setEditing(null);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore nel salvataggio"); } finally { setSaving(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Eliminare "${it[schema.fields[0].k] || it.descrizione || it.marca_modello}"?`)) return;
    try { await api.delete(`/risorse/${schema.kind}/${it.id}`); toast.success("Eliminato"); await load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="space-y-3" data-testid={`risorse-tab-${schema.kind}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="h-4 w-4 absolute left-2.5 top-3 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca…" className="h-10 pl-8" data-testid={`risorse-search-${schema.kind}`} />
        </div>
        <span className="text-xs text-slate-500">{filtered.length} / {items.length}</span>
        {isAdmin && (
          <Button size="sm" className="ml-auto bg-[#0044FF] hover:bg-[#0033CC]" onClick={() => setEditing(emptyFor(schema.fields))} data-testid={`risorse-add-${schema.kind}`}>
            <Plus className="h-4 w-4 mr-1" /> Nuovo
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 py-8 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Caricamento…</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>{cols.map((f) => <th key={f.k} className={`px-3 py-2 font-bold ${f.w || ""}`}>{f.l}</th>)}{isAdmin && <th className="px-3 py-2 w-20" />}</tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.length === 0 && <tr><td colSpan={cols.length + 1} className="px-3 py-6 text-center text-slate-400">Nessun elemento</td></tr>}
              {filtered.map((it) => (
                <tr key={it.id} className={`hover:bg-blue-50/40 ${it.attivo === false ? "opacity-50" : ""}`} data-testid={`risorse-row-${it.id}`}>
                  {cols.map((f) => <td key={f.k} className="px-3 py-2 align-top max-w-xs truncate" title={typeof it[f.k] === "string" ? it[f.k] : undefined}><CellValue f={f} item={it} /></td>)}
                  {isAdmin && (
                    <td className="px-2 py-1.5 text-right whitespace-nowrap">
                      <button className="p-1.5 rounded hover:bg-slate-100 text-slate-600" onClick={() => setEditing({ ...emptyFor(schema.fields), ...it })} data-testid={`risorse-edit-${it.id}`} title="Modifica"><Pencil className="h-4 w-4" /></button>
                      <button className="p-1.5 rounded hover:bg-red-50 text-red-600" onClick={() => remove(it)} data-testid={`risorse-delete-${it.id}`} title="Elimina"><Trash2 className="h-4 w-4" /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="risorse-form-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing?.id ? "Modifica" : "Nuovo"} · {schema.title}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {schema.fields.map((f) => <SchemaField key={f.k} f={f} value={editing[f.k]} onChange={(v) => setEditing((e) => ({ ...e, [f.k]: v }))} sedi={sedi} />)}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} data-testid="risorse-form-cancel">Annulla</Button>
            <Button onClick={save} disabled={saving} className="bg-[#0044FF] hover:bg-[#0033CC]" data-testid="risorse-form-save">{saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
